<template>
  <v-form class="mt-4">
    <v-card>
      <v-card-text>
        <v-menu
          ref="datepicker"
          v-model="showDatePicker"
          v-model:return-value="formData.date"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="290px"
          offset-y
        >
          <template #activator="{ props }">
            <v-text-field
              v-model="computedDateFormatted"
              label="Datum lijn"
              prepend-icon="mdi-calendar"
              readonly
              outlined
              required
              v-bind="props"
              dense
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="datePickerValue"
            no-title
            scrollable
            locale="nl-nl"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="showDatePicker = false"
              >Cancel</v-btn
            >
            <v-btn
              text
              color="primary"
              @click="$refs.datepicker.save(datePickerValue)"
              >OK</v-btn
            >
          </v-date-picker>
        </v-menu>

        <v-menu
          ref="timepicker"
          v-model="showTimePicker"
          v-model:return-value="formData.time"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="290px"
          offset-y
        >
          <template #activator="{ props }">
            <v-text-field
              v-model="formData.time"
              label="Tijd lijn"
              prepend-icon="mdi-clock"
              readonly
              outlined
              required
              v-bind="props"
              dense
            ></v-text-field>
          </template>
          <VTimePicker
            v-if="showTimePicker"
            v-model="formData.time"
            format="24hr"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="showTimePicker = false"
              >Cancel</v-btn
            >
            <v-btn
              text
              color="primary"
              @click="$refs.timepicker.save(formData.time)"
              >OK</v-btn
            >
          </VTimePicker>
        </v-menu>

        <v-select
          v-model="formData.line_category"
          :items="categories"
          label="Regel type"
          dense
          prepend-icon="mdi-cog"
          outlined
        ></v-select>
        <AtMentionBox
          v-model="formData.content"
          :instance_id="instance_id"
          class="ma-2"
        />
      </v-card-text>
    </v-card>
    <v-btn class="mt-2" block color="primary" @click="update">Opslaan</v-btn>
  </v-form>
</template>

<script>
import AtMentionBox from "../ui/AtMentionBox.vue";
import { useFormsStore } from "@/stores/formsStore";
import { useDayReportLinesStoreRefactor } from "@/stores/dayReportLinesStoreRefactor";
import moment from "moment";
import { ref, computed, onMounted } from "vue";

export default {
  name: "DayReportLineForm",
  components: {
    AtMentionBox,
  },
  props: ["item", "popup"],
  setup(props) {
    const formsStore = useFormsStore();
    const dayReportLinesStore = useDayReportLinesStoreRefactor();
    const showDatePicker = ref(false);
    const showTimePicker = ref(false);
    const loading = ref(false);
    const instance_id = ref(1);

    // Initialize form data with default values
    const formData = ref({
      id: null,
      day_report_id: null,
      line_category: "administration",
      content: "",
      date: moment().format("YYYY-MM-DD"),
      time: moment().format("HH:mm"),
    });

    // Populate form data when component is mounted
    onMounted(() => {
      console.log("Day report line item received:", props.item);

      if (props.item) {
        // Set basic properties
        formData.value.id = props.item.id || null;
        formData.value.day_report_id = props.item.day_report_id || null;
        formData.value.line_category =
          props.item.line_category || "administration";
        formData.value.content = props.item.content || "";

        // Times are always Amsterdam (NL). The server sends pre-formatted
        // Amsterdam strings (`date`, `time`); use them directly so the edited
        // values never depend on the device timezone. Only fall back to parsing
        // date_time (device-local) if those fields are absent.
        if (props.item.date) {
          formData.value.date = props.item.date;
        }
        if (props.item.time) {
          formData.value.time = props.item.time;
        }
        if (!props.item.date && !props.item.time && props.item.date_time) {
          const dateTimeMoment = moment(props.item.date_time);
          if (dateTimeMoment.isValid()) {
            formData.value.date = dateTimeMoment.format("YYYY-MM-DD");
            formData.value.time = dateTimeMoment.format("HH:mm");
          }
        }

        console.log("Form data initialized:", formData.value);
      }
    });

    const computedDateFormatted = computed(() => {
      return moment(formData.value.date).format("DD-MM-YYYY");
    });

    // Simple computed for date picker - converts Date object to YYYY-MM-DD string
    const datePickerValue = computed({
      get: () => formData.value.date,
      set: (value) => {
        formData.value.date =
          value instanceof Date ? moment(value).format("YYYY-MM-DD") : value;
      },
    });

    const categories = computed(() => {
      return Object.keys(dayReportLinesStore.icons);
    });

    return {
      formsStore,
      dayReportLinesStore,
      showDatePicker,
      showTimePicker,
      loading,
      formData,
      computedDateFormatted,
      datePickerValue,
      categories,
      instance_id,
    };
  },
  methods: {
    cancel() {
      if (this.popup) {
        this.formsStore.$reset();
        return;
      }
      this.$router.go(-1);
    },
    async update() {
      this.loading = true;
      try {
        // Ensure day_report_id is available for new lines
        if (!this.formData.day_report_id && this.item?.day_report_id) {
          this.formData.day_report_id = this.item.day_report_id;
        }

        // Make sure we have a valid day_report_id
        if (!this.formData.day_report_id) {
          console.error("Missing day_report_id for day report line");
          return;
        }

        const dateTime = moment(
          `${this.formData.date} ${this.formData.time}`,
          "YYYY-MM-DD HH:mm",
        );
        const updatedData = {
          ...this.formData,
          date_time: dateTime.format(),
        };

        console.log("Submitting day report line:", updatedData);

        if (this.formData.id) {
          // Update existing line
          await this.dayReportLinesStore.update(updatedData);
          console.log("Day report line updated");
        } else {
          // Create new line
          await this.dayReportLinesStore.create(updatedData);
          console.log("Day report line created");
        }

        this.formsStore.$reset();
      } catch (error) {
        console.error("Error updating day report line:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
